import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserState, Badge } from '../types';
import { auth, db, googleProvider } from '../firebase';
import { signInWithPopup, signOut, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';

const ADMIN_EMAIL = 'mkajanth@gmail.com';

const INITIAL_STATE: UserState = {
  name: '',
  isApproved: false,
  role: 'user',
  joinedAt: new Date().toISOString(),
  xp: 0,
  level: 1,
  badges: [],
  scenariosCompleted: 0,
  completedScenarioIds: [], // Initialize array
  assessmentsCompleted: 0,
  history: []
};

export const AVAILABLE_BADGES: Badge[] = [
  // Core
  { id: 'first_step', name: 'First Step', icon: 'Footprints', description: 'Completed your first scenario.' },
  { id: 'observer', name: 'The Observer', icon: 'Eye', description: 'Reached Level 3.' },
  { id: 'master', name: 'Nunchi Master', icon: 'Crown', description: 'Reached Level 10.' },
  { id: 'perfect_vibe', name: 'Vibe Checker', icon: 'Sparkles', description: 'Analyzed an atmosphere.' },
  
  // Ikigai
  { id: 'ikigai_seeker', name: 'Purpose Finder', icon: 'Flower', description: 'Synthesized your Ikigai in the Lab.' },
  { id: 'spark_igniter', name: 'Spark Igniter', icon: 'Zap', description: 'Completed a Daily Spark mission.' },
  
  // Zen
  { id: 'koan_solver', name: 'Koan Solver', icon: 'Gavel', description: 'Submitted an answer to the Zen Master.' },
  { id: 'mind_gardener', name: 'Mind Gardener', icon: 'Wind', description: 'Cleansed your mind in the Zen Garden.' },
  { id: 'zen_mind', name: 'Zen Mind', icon: 'Moon', description: 'Read a Zen story.' },

  // Ichigo Ichie
  { id: 'tea_master', name: 'Tea Master', icon: 'Coffee', description: 'Designed a mental Tea Ceremony.' },
  { id: 'time_traveler', name: 'Time Traveler', icon: 'Sunset', description: 'Immortalized a moment in the Golden Hour.' },

  // Letting Go
  { id: 'phoenix', name: 'The Phoenix', icon: 'Flame', description: 'Completed a Release Ceremony.' },
  { id: 'water_flow', name: 'Water Flow', icon: 'Droplets', description: 'Entered the Flow State.' },

  // Courage
  { id: 'task_separator', name: 'Task Separator', icon: 'Scissors', description: 'Separated your tasks from others.' },
  { id: 'goal_shifter', name: 'Goal Shifter', icon: 'Compass', description: 'Shifted from etiology to teleology.' },

  // Happy
  { id: 'love_architect', name: 'Love Architect', icon: 'Heart', description: 'Analyzed a Task of Love.' },
  { id: 'independent_spirit', name: 'Free Spirit', icon: 'UserCheck', description: 'Built a guide for Self-Reliance.' },

  // Kaizen
  { id: 'small_stepper', name: 'Small Stepper', icon: 'Footprints', description: 'Broke down a goal into Kaizen steps.' },
  { id: 'mirror_gazer', name: 'Mirror Gazer', icon: 'Layers', description: 'Practiced Hansei reflection.' },

  // Stoic
  { id: 'stoic_sage', name: 'Stoic Sage', icon: 'Scale', description: 'Practiced the Dichotomy of Control.' },
  { id: 'morning_warrior', name: 'Morning Warrior', icon: 'Sunrise', description: 'Prepared the mind with Morning Rehearsal.' },

  // Slow Down
  { id: 'pause_button', name: 'Pause Button', icon: 'PauseCircle', description: 'Hit the mindful pause button.' },
  { id: 'peacemaker', name: 'Peacemaker', icon: 'Users', description: 'Restored harmony in a relationship.' },
];

interface UserContextType {
  user: UserState;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  isAdmin: boolean;
  addXp: (amount: number, reason: string) => void;
  unlockBadge: (badgeId: string) => void;
  incrementStat: (key: 'scenariosCompleted' | 'assessmentsCompleted') => void;
  markScenarioSeen: (scenarioId: string) => void;
  updateName: (name: string) => void;
  resetProgress: () => void;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserState>(INITIAL_STATE);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  // 1. Listen for Auth Changes with Realtime DB Listeners
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setFirebaseUser(currentUser);
      
      if (currentUser) {
        const docRef = doc(db, "users", currentUser.uid);
        
        // Listen to realtime updates on the user document (for approval status changes)
        const unsubscribeSnapshot = onSnapshot(docRef, async (docSnap) => {
          if (docSnap.exists()) {
            const userData = docSnap.data() as UserState;
            // Ensure array exists for legacy users
            if (!userData.completedScenarioIds) userData.completedScenarioIds = [];
            setUser({ ...userData, uid: currentUser.uid });
          } else {
            // Create new profile for Google User
            const isSuperAdmin = currentUser.email === ADMIN_EMAIL;
            const newUserState: UserState = {
              ...INITIAL_STATE,
              uid: currentUser.uid,
              name: currentUser.displayName || 'Student',
              email: currentUser.email || '',
              isApproved: isSuperAdmin,
              role: isSuperAdmin ? 'admin' : 'user', 
            };
            await setDoc(docRef, newUserState);
            setUser(newUserState);
          }
          setLoading(false);
        });

        return () => unsubscribeSnapshot();
      } else {
        // Logged out
        setUser(INITIAL_STATE);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // 2. Persist State
  const saveUserState = async (newState: UserState) => {
    setUser(newState);
    if (firebaseUser) {
      const docRef = doc(db, "users", firebaseUser.uid);
      await updateDoc(docRef, newState as any);
    }
  };

  const login = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error("Login failed", error);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(INITIAL_STATE);
  };

  const updateName = (name: string) => {
    saveUserState({ ...user, name });
  };

  const addXp = (amount: number, reason: string) => {
    const newXp = user.xp + amount;
    const newLevel = Math.floor(newXp / 100) + 1;
    
    let newBadges = [...user.badges];
    if (newLevel >= 3 && !user.badges.find(b => b.id === 'observer')) {
      newBadges.push({ ...AVAILABLE_BADGES.find(b => b.id === 'observer')!, unlockedAt: new Date().toISOString() });
    }
    if (newLevel >= 10 && !user.badges.find(b => b.id === 'master')) {
      newBadges.push({ ...AVAILABLE_BADGES.find(b => b.id === 'master')!, unlockedAt: new Date().toISOString() });
    }

    saveUserState({
      ...user,
      xp: newXp,
      level: newLevel,
      badges: newBadges,
      history: [{ date: new Date().toISOString(), action: reason, xpGained: amount }, ...user.history].slice(0, 50)
    });
  };

  const unlockBadge = (badgeId: string) => {
    if (user.badges.find(b => b.id === badgeId)) return;
    const badge = AVAILABLE_BADGES.find(b => b.id === badgeId);
    if (!badge) return;
    
    // Toast or Notification could be added here
    console.log(`Unlocked Badge: ${badge.name}`);
    
    saveUserState({
      ...user,
      badges: [...user.badges, { ...badge, unlockedAt: new Date().toISOString() }]
    });
  };

  const incrementStat = (key: 'scenariosCompleted' | 'assessmentsCompleted') => {
    let newState = { ...user, [key]: user[key] + 1 };
    if (key === 'scenariosCompleted' && newState.scenariosCompleted === 1) {
      if (!user.badges.find(b => b.id === 'first_step')) {
         const badge = AVAILABLE_BADGES.find(b => b.id === 'first_step')!;
         newState.badges = [...newState.badges, { ...badge, unlockedAt: new Date().toISOString() }];
      }
    }
    saveUserState(newState);
  };

  // New function to track seen scenarios
  const markScenarioSeen = (scenarioId: string) => {
      if (user.completedScenarioIds.includes(scenarioId)) return;
      
      const newIds = [...user.completedScenarioIds, scenarioId];
      saveUserState({
          ...user,
          completedScenarioIds: newIds
      });
  };

  const resetProgress = () => {
    if (confirm("Are you sure you want to reset all progress? This cannot be undone.")) {
      const resetState = {
        ...INITIAL_STATE, 
        name: user.name,
        email: user.email,
        uid: user.uid,
        isApproved: user.isApproved,
        role: user.role
      };
      saveUserState(resetState);
      localStorage.removeItem('ikigai_pillars');
      localStorage.removeItem('last_spark_date');
      localStorage.removeItem('last_spark_text');
    }
  };

  const isAdmin = user.role === 'admin' || firebaseUser?.email === ADMIN_EMAIL;

  return (
    <UserContext.Provider value={{ user, firebaseUser, loading, isAdmin, addXp, unlockBadge, incrementStat, markScenarioSeen, updateName, resetProgress, login, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};